import { useCallback, useEffect, useRef, useState } from 'react';

interface UseMobileSheetDragParams {
  onClose: () => void;
}

interface UseMobileSheetDragReturn {
  dragY: number;
  isDragging: boolean;
  onDragHandlePointerDown: (e: React.PointerEvent) => void;
}

const DISMISS_THRESHOLD = 110;

export function useMobileSheetDrag({
  onClose,
}: UseMobileSheetDragParams): UseMobileSheetDragReturn {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const startYRef = useRef(0);
  const dragYRef = useRef(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handlePointerMove = useRef((e: PointerEvent) => {
    const dy = Math.max(0, e.clientY - startYRef.current);
    dragYRef.current = dy;
    setDragY(dy);
  });

  const handlePointerUp = useRef(() => {
    window.removeEventListener('pointermove', handlePointerMove.current);
    window.removeEventListener('pointerup', handlePointerUp.current);
    setIsDragging(false);
    const dy = dragYRef.current;
    dragYRef.current = 0;
    setDragY(0);
    if (dy > DISMISS_THRESHOLD) {
      onCloseRef.current();
    }
  });

  useEffect(() => {
    const moveHandler = handlePointerMove.current;
    const upHandler = handlePointerUp.current;
    return () => {
      window.removeEventListener('pointermove', moveHandler);
      window.removeEventListener('pointerup', upHandler);
    };
  }, []);

  const onDragHandlePointerDown = useCallback((e: React.PointerEvent) => {
    startYRef.current = e.clientY;
    setIsDragging(true);
    window.addEventListener('pointermove', handlePointerMove.current);
    window.addEventListener('pointerup', handlePointerUp.current);
  }, []);

  return { dragY, isDragging, onDragHandlePointerDown };
}
